<template>
  <div class="p-6 space-y-6">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <LayoutDashboard class="w-7 h-7 text-brand-500" />
          Panel General
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Métricas clave de atención, rendimiento de agentes y conversión comercial.
        </p>
      </div>

      <button
        @click="fetchDashboardData"
        :disabled="isLoading"
        class="px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 text-xs font-bold transition-all flex items-center gap-2 self-start md:self-auto shadow-xs"
      >
        <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': isLoading }" />
        Actualizar
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center py-20">
      <Loader2 class="w-8 h-8 text-brand-500 animate-spin" />
    </div>

    <div v-else class="space-y-6">
      <!-- 1. Fila de KPIs Principales -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Conversaciones -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 shadow-xs flex items-center justify-between">
          <div>
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wider">Conversaciones</p>
            <h3 class="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
              {{ stats.chats?.conversations || 0 }}
            </h3>
            <span class="text-[11px] text-slate-500">{{ stats.chats?.messages || 0 }} mensajes totales</span>
          </div>
          <div class="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <MessageSquare class="w-6 h-6" />
          </div>
        </div>

        <!-- Total Leads -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 shadow-xs flex items-center justify-between">
          <div>
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wider">Leads Calificados</p>
            <h3 class="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
              {{ stats.leads?.total || 0 }}
            </h3>
            <span class="text-[11px] text-emerald-600 font-semibold">{{ conversionRate }}% conversión a venta</span>
          </div>
          <div class="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
            <Target class="w-6 h-6" />
          </div>
        </div>

        <!-- Agentes Activos -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 shadow-xs flex items-center justify-between">
          <div>
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wider">Agentes</p>
            <h3 class="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
              {{ stats.agents?.total || 0 }}
            </h3>
            <span class="text-[11px] text-slate-500">{{ stats.agents?.active || 0 }} activos online</span>
          </div>
          <div class="w-12 h-12 rounded-2xl bg-brand-50 dark:bg-brand-950/40 text-white dark:text-brand-400 flex items-center justify-center">
            <BrainCircuit class="w-6 h-6" />
          </div>
        </div>

        <!-- Instancias WhatsApp -->
        <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 shadow-xs flex items-center justify-between">
          <div>
            <p class="text-xs font-bold text-slate-400 uppercase tracking-wider">Líneas WhatsApp</p>
            <h3 class="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
              {{ stats.agents?.instances || 0 }}
            </h3>
            <span class="text-[11px] text-slate-500">Instancias vinculadas</span>
          </div>
          <div class="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <Smartphone class="w-6 h-6" />
          </div>
        </div>
      </div>

      <!-- 2. Sección Central: Desglose del Pipeline & Total Facturado -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Pipeline Funnel -->
        <div class="lg:col-span-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-6 shadow-xs space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <TrendingUp class="w-5 h-5 text-brand-500" />
              Estado del Pipeline de Leads
            </h3>
            <router-link to="leads" class="text-xs font-bold text-brand-600 hover:text-brand-700">
              Ver todos →
            </router-link>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <div class="p-4 rounded-xl border border-yellow-200/60 bg-yellow-50/50 dark:bg-yellow-950/20 dark:border-yellow-800/40">
              <span class="text-xs font-bold text-yellow-700 dark:text-yellow-400 block">Nuevos</span>
              <p class="text-2xl font-black text-slate-900 dark:text-white mt-1">{{ stats.leads?.new || 0 }}</p>
            </div>

            <div class="p-4 rounded-xl border border-blue-200/60 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800/40">
              <span class="text-xs font-bold text-blue-700 dark:text-blue-400 block">Negociando</span>
              <p class="text-2xl font-black text-slate-900 dark:text-white mt-1">{{ stats.leads?.in_progress || 0 }}</p>
            </div>

            <div class="p-4 rounded-xl border border-emerald-200/60 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800/40">
              <span class="text-xs font-bold text-emerald-700 dark:text-emerald-400 block">Ganadas</span>
              <p class="text-2xl font-black text-slate-900 dark:text-white mt-1">{{ stats.leads?.won || 0 }}</p>
            </div>

            <div class="p-4 rounded-xl border border-red-200/60 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800/40">
              <span class="text-xs font-bold text-red-700 dark:text-red-400 block">Canceladas</span>
              <p class="text-2xl font-black text-slate-900 dark:text-white mt-1">{{ stats.leads?.lost || 0 }}</p>
            </div>
          </div>
        </div>

        <!-- Ingresos por Ventas Cerradas -->
        <div class="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 shadow-xs flex flex-col justify-between space-y-4">
          <div>
            <span class="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <DollarSign class="w-4 h-4 text-emerald-400" /> Total Ventas Cerradas
            </span>
            <h2 class="text-3xl font-black text-white mt-2">
              $ {{ formatCurrency(stats.leads?.revenue || 0) }}
            </h2>
            <p class="text-xs text-slate-400 mt-1">Acumulado generado por leads en estado ganado.</p>
          </div>

          <div class="pt-4 border-t border-slate-700/60 flex items-center justify-between text-xs">
            <span class="text-slate-400">Total Ganados:</span>
            <span class="font-bold text-emerald-400">{{ stats.leads?.won || 0 }} cierres</span>
          </div>
        </div>
      </div>

      <!-- 3. Tabla: Últimos Leads Capturados -->
      <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-6 shadow-xs space-y-4">
        <h3 class="text-base font-bold text-slate-900 dark:text-white">
          Últimos Prospectos Capturados
        </h3>

        <div v-if="!stats.recent_leads || stats.recent_leads.length === 0" class="text-center py-8 text-slate-400 text-xs">
          Aún no se han registrado prospectos recientes.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-left text-xs">
            <thead>
              <tr class="text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                <th class="pb-3 font-bold">Cliente</th>
                <th class="pb-3 font-bold">Teléfono</th>
                <th class="pb-3 font-bold">Instancia</th>
                <th class="pb-3 font-bold">Estado</th>
                <th class="pb-3 font-bold">Fecha</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-700/60 font-medium">
              <tr v-for="lead in stats.recent_leads" :key="lead.id" class="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                <td class="py-3 font-bold text-slate-900 dark:text-white">{{ lead.full_name || 'Prospecto' }}</td>
                <td class="py-3 text-slate-600 dark:text-slate-300">+{{ lead.phone }}</td>
                <td class="py-3 text-slate-500">{{ lead.instance_name }}</td>
                <td class="py-3">
                  <span class="px-2 py-0.5 rounded-md font-bold text-[10px]" :class="getStatusBadgeClass(lead.status)">
                    {{ getStatusLabel(lead.status) }}
                  </span>
                </td>
                <td class="py-3 text-slate-400">{{ formatDate(lead.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  LayoutDashboard, MessageSquare, Target, BrainCircuit, Smartphone,
  TrendingUp, DollarSign, RefreshCw, Loader2
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';

const { showError } = useToast();

const stats = ref({
  agents: { total: 0, instances: 0, active: 0 },
  leads: { total: 0, new: 0, in_progress: 0, won: 0, lost: 0, revenue: 0 },
  chats: { conversations: 0, messages: 0 },
  recent_leads: []
});
const isLoading = ref(true);

const currentUser = SecureStorage.getItem('neurals_user') || SecureStorage.getItem('llevo_user');

const fetchDashboardData = async () => {
  isLoading.value = true;
  try {
    const params = {
      owner: currentUser?.id,
      is_admin: currentUser?.role === 'admin'
    };
    const res = await apiClient.get('/get_dashboard_stats', { params });
    stats.value = res.data?.data || stats.value;
  } catch (err) {
    showError('No se pudieron cargar las estadísticas.');
  } finally {
    isLoading.value = false;
  }
};

const conversionRate = computed(() => {
  const total = stats.value.leads?.total || 0;
  const won = stats.value.leads?.won || 0;
  if (!total) return 0;
  return ((won / total) * 100).toFixed(1);
});

const formatCurrency = (val) => {
  return Number(val || 0).toLocaleString('es-NI', { minimumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-NI', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const getStatusLabel = (st) => {
  const map = { new: 'Nuevo', in_progress: 'Negociando', won: 'Ganada', lost: 'Cancelada' };
  return map[st] || st;
};

const getStatusBadgeClass = (st) => {
  const map = {
    new: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-400',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
    won: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    lost: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400'
  };
  return map[st] || 'bg-slate-100 text-slate-800';
};

onMounted(() => {
  fetchDashboardData();
});
</script>